"use client";

import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { assetService } from '@/services/asset-service';
import ReactMarkdown from 'react-markdown';

type AssetType = {
    name: string;
    label: string;
    description: string;
};

type Props = {
    assetTypes: AssetType[];
    onClose: () => void;
    onComplete: () => void;
};

// Filter types that are text-based
const getTextTypes = (types: AssetType[]): AssetType[] => {
    return types.filter(type =>
        type.name.startsWith('text/') ||
        type.name === 'application/json'
    );
};

export function TextAssetCreationForm({ assetTypes, onClose, onComplete }: Props) {
    const [selectedType, setSelectedType] = useState<string>('');
    const [assetName, setAssetName] = useState('');
    const [textContent, setTextContent] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const textTypes = getTextTypes(assetTypes);
    const selectedTypeObj = textTypes.find(t => t.name === selectedType);
    const isMarkdown = selectedType === 'text/markdown';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validation
        if (!selectedType) {
            toast.error('Please select an asset type');
            return;
        }
        if (!assetName.trim()) {
            toast.error('Please enter an asset name');
            return;
        }
        if (!textContent.trim()) {
            toast.error('Please enter content');
            return;
        }

        setIsSubmitting(true);
        try {
            // Upload text content to R2 and create asset with URI reference
            // This follows the same pattern as file uploads - content is stored in R2, not in database
            const filename = 'content.txt';
            await assetService.createAssetFromContent(
                textContent.trim(),
                assetName.trim(),
                selectedType,
                filename
            );

            toast.success('Text asset created successfully');
            onComplete();
        } catch (error) {
            console.error('Error creating text asset:', error);
            toast.error((error as Error).message || 'Failed to create text asset');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
                {/* Asset Type Selection */}
                <div className="space-y-2">
                    <Label htmlFor="type-select">Asset Type</Label>
                    <Select value={selectedType} onValueChange={setSelectedType}>
                        <SelectTrigger id="type-select">
                            <SelectValue placeholder="Select text asset type" />
                        </SelectTrigger>
                        <SelectContent>
                            {textTypes.map(type => (
                                <SelectItem key={type.name} value={type.name}>
                                    {type.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {selectedTypeObj?.description && (
                        <p className="text-sm text-gray-500">{selectedTypeObj.description}</p>
                    )}
                </div>

                {/* Asset Name */}
                <div className="space-y-2">
                    <Label htmlFor="name-input">Asset Name</Label>
                    <Input
                        id="name-input"
                        placeholder="e.g., API Documentation"
                        value={assetName}
                        onChange={(e) => setAssetName(e.target.value)}
                    />
                </div>

                {/* Content Editor */}
                {isMarkdown ? (
                    <div className="space-y-2">
                        <Label>Content</Label>
                        <Tabs defaultValue="editor" className="w-full">
                            <TabsList>
                                <TabsTrigger value="editor">Editor</TabsTrigger>
                                <TabsTrigger value="preview">Preview</TabsTrigger>
                            </TabsList>
                            <TabsContent value="editor" className="space-y-2">
                                <Textarea
                                    placeholder="Write your markdown here..."
                                    value={textContent}
                                    onChange={(e) => setTextContent(e.target.value)}
                                    className="font-mono text-sm min-h-64"
                                />
                            </TabsContent>
                            <TabsContent value="preview" className="space-y-2">
                                <div className="border rounded-md p-4 min-h-64 bg-gray-50 prose prose-sm max-w-none">
                                    <ReactMarkdown>{textContent || '(Preview will appear here)'}</ReactMarkdown>
                                </div>
                            </TabsContent>
                        </Tabs>
                    </div>
                ) : (
                    <div className="space-y-2">
                        <Label htmlFor="content-input">Content</Label>
                        <Textarea
                            id="content-input"
                            placeholder="Enter your content..."
                            value={textContent}
                            onChange={(e) => setTextContent(e.target.value)}
                            className={isMarkdown ? "font-mono text-sm" : ""}
                            style={{ minHeight: isMarkdown ? '400px' : '200px' }}
                        />
                    </div>
                )}

                {/* Character Counter */}
                <div className="text-sm text-gray-500">
                    {textContent.length} characters
                </div>

                {/* Privacy Notice */}
                <Alert>
                    <AlertDescription>
                        🔒 <strong>Personal Asset:</strong> Only you can see this asset. You can share it with your organization later.
                    </AlertDescription>
                </Alert>
            </div>

            {/* Actions */}
            <div className="flex gap-2 justify-end">
                <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    disabled={isSubmitting || !selectedType || !assetName.trim() || !textContent.trim()}
                >
                    {isSubmitting ? 'Creating...' : 'Create Asset'}
                </Button>
            </div>
        </form>
    );
}
